import logging from '@sstdev/lib_logging';
import debouncePromise from 'debounce-promise';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import { createElement as rc, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
const { set, cloneDeep } = lodash;

import { ScrollView, contexts, fromTheme, h4, hooks, styled } from 'lib_ui-primitives';
import { globalConfig, metadata, uiRuntimeConfig } from 'lib_ui-services';
import HNode from '../../HNode';
import useDbView from '../../hooks/useDbView';
import useNetwork from '../../hooks/useNetwork';
import buildIncludedValueCriterion from '../../utilities/buildIncludedValueCriterion';
import createTreePositionGetter from '../../utilities/createTreePositionGetter';
import ActiveRecord from '../contextProviders/ActiveRecord';
import FilterInterdependencyBoundary from '../contextProviders/FilterInterdependencyBoundary';
import LoadingBoundary from '../contextProviders/LoadingBoundary';
import SearchPaneHeader from './SearchPaneHeader';

const { useRouter } = hooks;
const { getRelationMetadata } = metadata;

const _SearchPane = styled(ScrollView).attrs({ name: 'search-pane' })`
    background-color: ${({ theme, horizontalLayout }) => (horizontalLayout ? theme.backgroundColor : 'transparent')};
    height: 100%;
    width: ${props => (props.horizontalLayout ? '100%' : '300px')};
    flex-direction: ${props => (props.horizontalLayout ? 'row' : 'column')};
    flex-wrap: ${props => (props.horizontalLayout ? 'wrap' : 'nowrap')};
    flex-basis: auto;
    padding-left: ${fromTheme('viewPadding')};
    padding-bottom: ${({ theme }) => theme.mobile ? `${theme.formField?.padding?.bottom ?? 4}px` : '0'};
    border-radius: ${fromTheme('searchPane', 'borderRadius')};

    & [name='range-search-element'] {
        margin: ${fromTheme('searchPane', 'rangeSearchElement', 'margin')};
        background: ${fromTheme('backgroundColor')};
        border-radius: ${fromTheme('searchPane', 'rangeSearchElement', 'borderRadius')};
    }
    & [name='range-field-container'] {
        margin: ${fromTheme('searchPane', 'rangeFieldContainer', 'margin')};
    }

    ${({ theme }) => `
        & [name='form-field'] {
            margin-top: ${theme.searchPane.formField?.marginTop}px;
            margin-right: ${theme.searchPane.formField?.marginRight}px;
        }
        & [name='multi-picker-dropdown'] {
            margin-right: 0;
        }
        & [name='range-search-element']+[name='multi-picker-dropdown'] {
            margin-top: ${theme.searchPane.multiPickerDropdown?.marginTop ?? 0}px;
        } 
    `}
`;

const SearchPaneTitle = styled(h4)`
    margin: 6px;
    color: ${({ theme }) => theme.defaultFontColor};
`;

const _p = {
    useDbView,
    getRelationMetadata,
    useNetwork,
    getRecalcMethod,
    globalConfig,
    uiRuntimeConfig
};
export const _private = _p;
/**
 * @typedef {Object} Props
 * @property {Object} hNode
 * @property {string} currentRoute
 */
/** @type {import('react').FC<Props>} */
function SearchPane(props) {
    const {
        hNode,
        hNode: {
            id,
            namespace,
            relation,
            title,
            verticalLayout,
            horizontalLayout = !verticalLayout && !title,
            children: childHNodes = [],
            treePosition
        },
        testChild
    } = props || { hNode: {} };
    const childFilters = useRef({});
    const pendingRecalc = useRef(false);
    const searchCleanups = useRef([]);
    const { isOnline } = _p.useNetwork();
    const router = useRouter();
    const routePath = router.getRouteStateKey();

    // SearchPane typically will filter a list type control (e.g. Grid)
    // that needs a lokijs dynamicView and corresponding viewCriteria.
    // This will get that viewCriteria.
    const { viewCriteria, viewReady } = _p.useDbView(namespace, relation, undefined, hNode, undefined, false);
    // Avoid getting a new ref for this everytime.
    const recalcFilters = useMemo(
        () => _p.getRecalcMethod(viewCriteria, id, isOnline, namespace, relation, routePath),
        [viewCriteria, id, isOnline, namespace, relation, routePath]
    );

    // If the route changes, then force a recalc so that filters specific to the previous
    // route will be removed.
    useEffect(() => {
        if (viewReady) {
            recalcFilters(childFilters.current[routePath] ?? {}).then(cleanup => {
                if (cleanup != null) {
                    searchCleanups.current.push(cleanup);
                }
            });
        } else {
            pendingRecalc.current = true;
        }
        // This should only be executed if routePath changes (and recalcFilters will
        // be changed if that happens)
        // eslint-disable-next-line
    }, [recalcFilters]);

    // This valueChanged callback is passed down into filtering controls
    // like DropDownSearch.
    // It will recalculate the filters for the list viewCriteria here based on
    // selections in those filtering controls.
    const valueChanged = useCallback(
        (childHNode, filter) => {
            if (childHNode == null || childHNode.hNodeType == null) {
                throw new Error(
                    'A searchElement must fire a valueChange with an hNode as the first argument and the value as the second'
                );
            }
            logging.debug(
                `[SEARCH_PANE] valueChanged ${JSON.stringify(
                    {
                        routePath,
                        child: { id: childHNode.id, titular: childHNode.title ?? childHNode.assetNo },
                        filter
                    },
                    null,
                    3
                )}`
            );
            set(childFilters.current, [routePath, childHNode.id], { hNode: childHNode, filter });
            // Defer the recalc (see below) if the dynamicView isn't ready yet
            if (viewReady) {
                recalcFilters(childFilters.current[routePath] ?? {}).then(cleanup => {
                    if (cleanup != null) {
                        searchCleanups.current.push(cleanup);
                    }
                });
            } else {
                pendingRecalc.current = true;
            }
        },
        [viewReady, recalcFilters, routePath]
    );

    useEffect(() => {
        if (viewReady && pendingRecalc.current) {
            try {
                recalcFilters(childFilters.current[routePath] ?? {}).then(cleanup => {
                    if (cleanup != null) {
                        searchCleanups.current.push(cleanup);
                    }
                });
            } catch (err) {
                logging.error(err);
            } finally {
                pendingRecalc.current = false;
            }
        }
    }, [viewReady, recalcFilters, routePath]);

    useEffect(() => {
        const _searchCleanups = searchCleanups.current;
        return () => {
            _searchCleanups.forEach(cleanup => cleanup());
        };
    }, []);

    const [iteration, forceRender] = useReducer(s => s + 1, 0);

    const resetFilter = () => {
        childFilters.current = {};
    };

    const searchElementChildren = useMemo(
        () => childHNodes.filter(child => child.hNodeTypeGroup === 'searchElement'),
        [childHNodes]
    );
    const searchElementIds = useMemo(() => getChildIds(searchElementChildren), [searchElementChildren]);

    const otherChildren = useMemo(
        () => childHNodes.filter(child => child.hNodeTypeGroup !== 'searchElement'),
        [childHNodes]
    );
    const addHeader = !!otherChildren.length;

    const getTreePosition = createTreePositionGetter(treePosition, searchElementChildren.length);

    // prettier-ignore
    return rc(FilterInterdependencyBoundary, { name: `search-pane-${id}`, restoreSelectionsOnRemount: true },
        rc(contexts.SearchContext.Provider, { value: valueChanged },
            rc(_SearchPane, { ...props, id, horizontalLayout },
                title && title.length ? rc(SearchPaneTitle, { name: 'search-pane-title' }, title) : null,
                addHeader && rc(LoadingBoundary, null,
                    rc(ActiveRecord, { namespace: 'application', relation: 'savedFilter', isNew: true },
                        rc(SearchPaneHeader,{
                            ...props,
                            hNode:{...props.hNode, children:otherChildren},
                            searchElementIds,
                            onLoad:forceRender,
                            resetFilter,
                            currentRoute: routePath
                        })
                    )
                ),
                ...searchElementChildren.map((hNode, index) => {
                    hNode.treePosition = getTreePosition(index);
                    return rc(HNode, { hNode, key: `${index}-${iteration}`, currentRoute:routePath });
                }),
                //just for unit testing:
                testChild
            )
        )
    );
}

SearchPane.defaultProps = {};

SearchPane.propTypes = {
    hNode: PropTypes.object.isRequired,
    currentRoute: PropTypes.string.isRequired
};

export default SearchPane;

function getRecalcMethod(viewCriteria, filterId, isOnline, namespace, relation, routePath) {
    return debouncePromise(
        async childFilterValues => {
            const filters = {};
            // Build one searchCriteria object by combining all search element outputs,
            // including special aggregation for IncludedValueSearch.
            const searchCriteria = buildSearchCriteria(childFilterValues);
            if (searchCriteria && Object.keys(searchCriteria).length) {
                filters.searchCriteria = searchCriteria;
            }

            // If switching between routes, remove any filters associated with a previous
            // route.
            viewCriteria.removeFiltersFromOtherRoutes(routePath);
            viewCriteria.applyFilters(filters, filterId, routePath);
        },
        _p.uiRuntimeConfig.getOnlineSearchDebounceMilliseconds(),
        { accumulate: false }
    );
}

function getChildIds(root) {
    if (Array.isArray(root)) {
        return root.flatMap(getChildIds);
    }
    const { id, hNodeType, propertyName, children } = root;
    const ownIds = hNodeType === 'Range' ? [`from-${propertyName}-${id}`, `to-${propertyName}-${id}`] : [root.id];
    if (!children || !children.length) return ownIds;
    return [...ownIds, ...children.flatMap(child => getChildIds(child))];
}

// Combine child search element filters into one searchCriteria object.
// - Aggregates IncludedValueSearch entries across multiple toggles for the same property (union of values)
// - If any toggle for a property requests includeUndefined, include an $or clause for that property
// - Combines independent child criteria with AND semantics, preserving each child's $or group
function buildSearchCriteria(childFilterValues) {
    if (!childFilterValues) return {};
    const entries = Object.values(childFilterValues);
    if (!entries.length) return {};

    const includedGroups = new Map();
    /** @type {Record<string, any>} */
    const criteriaMap = {};
    const orGroups = [];

    entries.forEach(({ hNode, filter }) => {
        const iv = filter && filter.__includedValueSearch;
        if (hNode?.hNodeType === 'IncludedValueSearch' && iv) {
            const { propertyPath, values = [], includeUndefined = false, filterWrapper = x => x } = iv;
            if (!propertyPath) return;
            const group = includedGroups.get(propertyPath) || {
                values: new Set(),
                includeUndefined: false,
                filterWrapper
            };
            values.forEach(v => {
                if (v != null && `${v}`.trim() !== '') group.values.add(`${v}`.trim());
            });
            group.includeUndefined = group.includeUndefined || !!includeUndefined;
            includedGroups.set(propertyPath, group);
        } else if (filter && Object.keys(filter).length) {
            // Merge other search element filters into criteriaMap
            Object.entries(filter).forEach(([k, v]) => {
                if (k === '$or') {
                    if (!Array.isArray(v)) return;
                    orGroups.push({ $or: cloneDeep(v) });
                } else if (k === '$and' && Array.isArray(v)) {
                    orGroups.push(...cloneDeep(v));
                } else {
                    if (criteriaMap[k]?.$elemMatch && v?.$elemMatch) {
                        // Merge $elemMatch objects
                        criteriaMap[k].$elemMatch = {
                            ...cloneDeep(criteriaMap[k].$elemMatch),
                            ...v.$elemMatch
                        };
                    } else {
                        criteriaMap[k] = { ...cloneDeep(v) };
                    }
                }
            });
        }
    });

    // Translate IncludedValueSearch groups into criteria
    for (const [propertyPath, { values, includeUndefined, filterWrapper }] of includedGroups.entries()) {
        const vals = Array.from(values);
        const criterion = buildIncludedValueCriterion(propertyPath, vals, includeUndefined, filterWrapper);
        if (!criterion) continue;
        if (criterion.$or) {
            if (!criteriaMap.$and) criteriaMap.$and = [];
            criteriaMap.$and.push(criterion);
        } else {
            // criterion is { [propertyPath]: { $eq/$in } }
            const [[k, v]] = Object.entries(criterion);
            criteriaMap[k] = v;
        }
    }

    if (orGroups.length) {
        criteriaMap.$and = [...(criteriaMap.$and ?? []), ...orGroups];
    }

    return criteriaMap;
}
